<script setup>
import {
  useTemplateRef,
  onBeforeUnmount,
  computed,
  ref,
  watch,
  nextTick,
  useId,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { FILTER_ICON_CHOICES } from 'dashboard/components/widgets/conversation/conversationQuickFilters';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isSaving: { type: Boolean, default: false },
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
  contained: {
    type: Boolean,
    default: false,
  },
  // SiteUp: create from Atalhos (+) — Save shortcut + Cancel only + icon picker.
  shortcutMode: {
    type: Boolean,
    default: false,
  },
  // Ícone atual da pasta em edição (a troca é gravada pelo pai nos Atalhos)
  folderIcon: {
    type: String,
    default: '',
  },
  // Nomes das OUTRAS pastas, para o aviso de nome repetido (não bloqueia)
  existingFolderNames: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'applyFilter',
  'saveFilter',
  'updateFolder',
  'deleteFolder',
  'close',
]);
const { filterTypes } = useConversationFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
const { t } = useI18n();
const store = useStore();

const isEditingFolder = computed(
  () => props.isFolderView && !props.shortcutMode
);
const initialIcon = () =>
  (isEditingFolder.value && props.folderIcon) ||
  FILTER_ICON_CHOICES[0] ||
  'i-ph-folder';

// Criar pelo + dos Atalhos é uma pasta nova: não herda o nome da pasta aberta
const folderNameLocal = ref(props.shortcutMode ? '' : props.folderName);
const selectedIcon = ref(initialIcon());
const nameError = ref('');
watch(
  () => [props.shortcutMode, props.isFolderView],
  () => {
    folderNameLocal.value = props.shortcutMode ? '' : props.folderName;
    selectedIcon.value = initialIcon();
    nameError.value = '';
  }
);
watch(folderNameLocal, () => {
  nameError.value = '';
});
// Nome só com espaços vira chip sem texto: vale o nome sem as bordas
const trimmedFolderName = computed(() => (folderNameLocal.value || '').trim());

// Filtrar é a tarefa diária: nome e ícone só aparecem quando a pessoa pede
// para salvar. Criar pelos Atalhos ou editar uma pasta já é salvar.
const isSaveFormOpen = ref(false);
const showFolderForm = computed(
  () => props.shortcutMode || props.isFolderView || isSaveFormOpen.value
);

const folderNameFieldRef = useTemplateRef('folderNameFieldRef');
const focusFolderName = async () => {
  await nextTick();
  folderNameFieldRef.value?.querySelector('input')?.focus();
};
const openSaveForm = () => {
  isSaveFormOpen.value = true;
  focusFolderName();
};

// Botão ativo mesmo sem nome: clicar explica o que falta em vez de ficar cinza
const requireFolderName = () => {
  if (trimmedFolderName.value) return true;
  nameError.value = t('FILTER.CUSTOM_VIEWS.ADD.ERROR_MESSAGE');
  focusFolderName();
  return false;
};

const normalizeName = name => (name || '').trim().toLocaleLowerCase();
const isDuplicateName = computed(() => {
  const name = normalizeName(folderNameLocal.value);
  if (!name) return false;
  return props.existingFolderNames.some(
    existing => normalizeName(existing) === name
  );
});
const nameMessage = computed(() => {
  if (nameError.value) return nameError.value;
  return isDuplicateName.value
    ? t('FILTER.CUSTOM_VIEWS.ADD.DUPLICATE_NAME')
    : '';
});
const nameMessageType = computed(() => (nameError.value ? 'error' : 'info'));

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (!requireFolderName() || !isConditionsValid()) return;
  emit(
    'updateFolder',
    filters.value,
    trimmedFolderName.value,
    selectedIcon.value
  );
};

const saveAndOpen = () => {
  if (!requireFolderName() || !isConditionsValid()) {
    return;
  }

  emit(
    'saveFilter',
    filters.value,
    trimmedFolderName.value,
    selectedIcon.value
  );
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  if (props.shortcutMode) {
    return t('FILTER.CUSTOM_VIEWS.ADD.TITLE');
  }
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

// Leitor de tela anunciava "i-ph-folder": cada ícone ganha um nome legível
const iconLabelKey = icon =>
  icon.replace(/^i-ph-/, '').replace(/-/g, '_').toUpperCase();
const iconChoices = computed(() =>
  FILTER_ICON_CHOICES.map(icon => ({
    icon,
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    label: t(`FILTER.CUSTOM_VIEWS.ADD.ICONS.${iconLabelKey(icon)}`),
  }))
);
const iconLabelId = useId();

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  {
    ignore: [
      '#toggleConversationFilterButton',
      '[data-testid="quick-filter-create-custom-atalhos"]',
      '[data-testid="quick-filter-more"]',
    ],
  },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    data-testid="conversation-filter-drawer"
    class="z-40 w-full border border-n-weak"
    :class="
      props.contained
        ? 'flex h-full min-h-0 max-h-full max-w-full flex-col overflow-hidden rounded-2xl bg-n-solid-1'
        : 'grid max-w-3xl content-start gap-6 overflow-visible rounded-2xl bg-n-alpha-3 p-6 shadow-lg backdrop-blur-[100px] lg:w-[750px]'
    "
  >
    <div
      class="flex items-center justify-between gap-2"
      :class="
        props.contained ? 'shrink-0 border-b border-n-weak px-3 py-2.5' : ''
      "
    >
      <h3 class="text-base font-medium leading-6 text-n-slate-12">
        {{ filterModalHeaderTitle }}
      </h3>
      <Button
        v-if="props.contained"
        xs
        ghost
        slate
        icon="i-ph-x"
        :aria-label="t('FILTER.CANCEL_BUTTON_LABEL')"
        data-testid="conversation-filter-close"
        @click="emit('close')"
      />
    </div>

    <div
      :class="
        props.contained
          ? 'flex min-h-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto px-3 py-3'
          : 'contents'
      "
    >
      <ul class="reset-base grid list-none gap-4">
        <template v-for="(filter, index) in filters" :key="filter.id">
          <ConditionRow
            v-if="index === 0"
            ref="conditionsRef"
            :key="`filter-${filter.attributeKey}-0`"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:values="filter.values"
            :filter-types="filterTypes"
            :show-query-operator="false"
            :contained="props.contained"
            @remove="removeFilter(index)"
          />
          <ConditionRow
            v-else
            :key="`filter-${filter.attributeKey}-${index}`"
            ref="conditionsRef"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:query-operator="filters[index - 1].queryOperator"
            v-model:values="filter.values"
            show-query-operator
            :filter-types="filterTypes"
            :contained="props.contained"
            @remove="removeFilter(index)"
          />
        </template>
      </ul>
      <Button
        v-if="props.contained"
        sm
        ghost
        blue
        class="w-full justify-center"
        @click="addFilter"
      >
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>

      <div
        v-if="showFolderForm"
        class="grid gap-4 border-t border-n-weak"
        :class="props.contained ? 'pt-3' : 'pt-6'"
        data-testid="filter-folder-form"
      >
        <div ref="folderNameFieldRef">
          <Input
            v-model="folderNameLocal"
            :label="
              isEditingFolder
                ? t('FILTER.FOLDER_LABEL')
                : t('FILTER.CUSTOM_VIEWS.ADD.LABEL')
            "
            :placeholder="t('FILTER.CUSTOM_VIEWS.ADD.PLACEHOLDER')"
            :message="nameMessage"
            :message-type="nameMessageType"
            :aria-invalid="Boolean(nameError) || undefined"
          />
          <!-- O aviso do Input é só visual: aqui o leitor de tela ouve por que não salvou -->
          <span
            class="sr-only"
            role="status"
            aria-live="polite"
            data-testid="filter-folder-name-status"
          >
            {{ nameMessage }}
          </span>
        </div>
        <div data-testid="filter-icon-picker">
          <p
            :id="iconLabelId"
            class="mb-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('FILTER.CUSTOM_VIEWS.ADD.ICON_LABEL') }}
          </p>
          <div
            role="group"
            :aria-labelledby="iconLabelId"
            class="grid max-h-40 w-full grid-cols-[repeat(auto-fill,minmax(2.25rem,1fr))] gap-1 overflow-y-auto rounded-xl border border-n-weak bg-n-slate-2 p-1.5"
          >
            <button
              v-for="choice in iconChoices"
              :key="choice.icon"
              type="button"
              class="inline-flex size-9 items-center justify-center justify-self-center rounded-md transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
              :class="
                selectedIcon === choice.icon
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
              "
              :aria-label="choice.label"
              :title="choice.label"
              :aria-pressed="selectedIcon === choice.icon"
              :data-testid="`filter-icon-${choice.icon}`"
              @click="selectedIcon = choice.icon"
            >
              <Icon :icon="choice.icon" class="size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex gap-3 border-t border-n-weak"
      :class="
        props.contained
          ? 'shrink-0 flex-col px-3 py-3'
          : 'flex-wrap justify-between pt-3'
      "
    >
      <Button
        v-if="!props.contained && !shortcutMode"
        sm
        ghost
        blue
        @click="addFilter"
      >
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div
        class="gap-2"
        :class="props.contained ? 'grid w-full' : 'flex flex-wrap'"
      >
        <template v-if="shortcutMode">
          <Button
            sm
            solid
            blue
            :disabled="isSaving"
            :is-loading="isSaving"
            :class="{ 'w-full justify-center': props.contained }"
            data-testid="filter-save-shortcut"
            @click="saveAndOpen"
          >
            {{ t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON') }}
          </Button>
          <Button
            sm
            faded
            slate
            :class="{ 'w-full justify-center': props.contained }"
            data-testid="filter-cancel-shortcut"
            @click="emit('close')"
          >
            {{ t('FILTER.CUSTOM_VIEWS.ADD.CANCEL_BUTTON') }}
          </Button>
        </template>
        <template v-else-if="isFolderView">
          <Button
            sm
            solid
            blue
            :disabled="isSaving"
            :is-loading="isSaving"
            :class="{ 'w-full justify-center': props.contained }"
            data-testid="filter-update-folder"
            @click="updateSavedCustomViews"
          >
            {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
          </Button>
          <Button
            sm
            faded
            slate
            :class="{ 'w-full justify-center': props.contained }"
            @click="resetFilter"
          >
            {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
          </Button>
          <!-- Excluir mora aqui dentro, longe do lápis do cabeçalho -->
          <Button
            sm
            ghost
            ruby
            icon="i-ph-trash"
            :disabled="isSaving"
            :class="{ 'w-full justify-center': props.contained }"
            data-testid="filter-delete-folder"
            @click="emit('deleteFolder')"
          >
            {{ t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON') }}
          </Button>
        </template>
        <template v-else>
          <Button
            sm
            :variant="isSaveFormOpen ? 'faded' : 'solid'"
            :color="isSaveFormOpen ? 'slate' : 'blue'"
            :class="{ 'w-full justify-center': props.contained }"
            data-testid="filter-apply"
            @click="validateAndSubmit"
          >
            {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
          </Button>
          <Button
            v-if="isSaveFormOpen"
            sm
            solid
            blue
            :disabled="isSaving"
            :is-loading="isSaving"
            :class="{ 'w-full justify-center': props.contained }"
            data-testid="filter-save-folder"
            @click="saveAndOpen"
          >
            {{ t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON') }}
          </Button>
          <Button
            v-else
            sm
            ghost
            blue
            icon="i-ph-folder-simple-plus"
            :class="{ 'w-full justify-center': props.contained }"
            data-testid="filter-save-as-folder"
            @click="openSaveForm"
          >
            {{ t('FILTER.CUSTOM_VIEWS.ADD.SAVE_AS_FOLDER_BUTTON') }}
          </Button>
          <Button
            sm
            faded
            slate
            :class="{ 'w-full justify-center': props.contained }"
            @click="resetFilter"
          >
            {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
          </Button>
        </template>
      </div>
    </div>
  </div>
</template>
